import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AiAvatar } from './AiMessageBubble';
import AiRichText from './AiRichText';
// ~36 reveals/sec: fast enough to keep pace with the stream, slow enough to read
// as typing rather than as text appearing all at once.
const REVEAL_MS = 28;
// Each tick reveals this fraction of the outstanding buffer, so a big burst
// drains quickly while a trickle still moves a character at a time.
const CATCHUP_FRACTION = 16;
/**
 * Drop a trailing `**` that hasn't been closed yet, so a bold label appears
 * complete instead of as asterisks that resolve a moment later.
 */
function tidy(text) {
    const marks = text.split('**').length - 1;
    if (marks % 2 === 0)
        return text;
    return text.slice(0, text.lastIndexOf('**'));
}
const AiStreamingMessage = ({ text }) => {
    const [shown, setShown] = useState(0);
    // Read inside the interval so the timer is created once, not on every chunk.
    const textRef = useRef(text);
    textRef.current = text;
    useEffect(() => {
        // Under reduced-motion the reveal animation is the thing being objected
        // to, so text goes straight up as it arrives.
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (reduced) {
            setShown(text.length);
            return undefined;
        }
        const id = window.setInterval(() => {
            setShown((n) => {
                const full = textRef.current.length;
                if (n >= full)
                    return n;
                return Math.min(full, n + Math.max(1, Math.ceil((full - n) / CATCHUP_FRACTION)));
            });
        }, REVEAL_MS);
        return () => window.clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Keep up under reduced-motion, where there is no interval doing it.
    useEffect(() => {
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (reduced)
            setShown(text.length);
    }, [text]);
    const visible = tidy(text.slice(0, shown));
    return (_jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'flex-start' }, children: [_jsx(AiAvatar, {}), _jsxs(Box, { sx: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }, children: [_jsx(Typography, { sx: { fontSize: '12px', fontWeight: 600, color: '#344054' }, children: "G8A AI Agent" }), _jsxs(Box, { sx: { bgcolor: '#FFFFFF', border: '1px solid #EAECF0', borderRadius: '2px 12px 12px 12px', px: 1.5, py: 1.25 }, children: [_jsx(AiRichText, { text: visible }), _jsx(Box, { sx: {
                                    display: 'inline-block', width: '2px', height: '13px', mt: '2px',
                                    bgcolor: '#6172F3', verticalAlign: 'text-bottom',
                                    animation: 'aiCaret 1s step-end infinite',
                                    '@keyframes aiCaret': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } },
                                    '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.6 },
                                } })] }), _jsx(Typography, { sx: { fontSize: '11px', color: '#98A2B3' }, children: "Writing the analysis \u2014 suggestions follow when it finishes." })] })] }));
};
export default AiStreamingMessage;
